import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import styles from "./NotFoundPage.module.css";
import Meta from "../../components/SEO/Meta";
import { Home, Search } from "lucide-react";

const NotFoundPage = () => {
  const navigate = useNavigate();

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
  };

  const fadeInLeft = {
    initial: { opacity: 0, x: -30 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
  };

  const fadeInRight = {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleBrowseServices = () => {
    navigate("/solicita-serviciu");
  };

  return (
    <>
      <Meta
        title="Pagina nu a fost găsită - 404 | eisservice.ro"
        description="Pagina pe care o căutați nu există. Reveniți la pagina principală sau căutați serviciile disponibile pe eisservice.ro."
        url="https://eisservice.ro/404"
      />
      <div className={styles.notFoundPage}>
        {/* Hero Section */}
        <motion.div
          className={styles.heroSection}
          initial="initial"
          animate="animate"
          variants={fadeInUp}
        >
          <div className={styles.heroContainer}>
            <motion.div
              className={styles.errorCode}
              variants={fadeInUp}
              initial="initial"
              animate="animate"
            >
              404
            </motion.div>
            <motion.h1 className={styles.heroTitle} variants={fadeInUp}>
              Pagina nu a fost găsită
            </motion.h1>
            <motion.p className={styles.heroDescription} variants={fadeInUp}>
              Ne pare rău, pagina pe care o căutați nu există sau a fost mutată.
              Te rugăm să revii la pagina principală sau să explorezi serviciile
              disponibile.
            </motion.p>
            <motion.div
              className={styles.heroActions}
              variants={fadeInUp}
              initial="initial"
              animate="animate"
            >
              <button
                className={styles.primaryButton}
                onClick={handleGoHome}
                aria-label="Mergi la pagina principală"
              >
                <Home size={20} />
                Pagina Principală
              </button>
              <button
                className={styles.secondaryButton}
                onClick={handleBrowseServices}
                aria-label="Vezi serviciile disponibile"
              >
                <Search size={20} />
                Vezi Servicii
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Helpful Links Section */}
        <motion.section
          className={styles.helpfulSection}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeInUp}
        >
          <div className={styles.sectionContainer}>
            <motion.div className={styles.helpfulHeader} variants={fadeInUp}>
              <h2 className={styles.sectionTitle}>Poate te interesează</h2>
              <p className={styles.sectionSubtitle}>
                Explorează aceste pagini populare pentru a găsi ceea ce cauți
              </p>
            </motion.div>
            <motion.div className={styles.helpfulGrid} variants={fadeInUp}>
              <motion.div
                className={styles.helpfulCard}
                variants={fadeInLeft}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                onClick={handleBrowseServices}
              >
                <div className={styles.cardIcon}>
                  <Search size={28} />
                </div>
                <h3 className={styles.cardTitle}>Solicită un Serviciu</h3>
                <p className={styles.cardDescription}>
                  Găsește rapid profesioniști verificați pentru orice serviciu
                  de care ai nevoie
                </p>
              </motion.div>

              <motion.div
                className={styles.helpfulCard}
                variants={fadeInUp}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                onClick={() => navigate("/despre")}
              >
                <div className={styles.cardIcon}>
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <h3 className={styles.cardTitle}>Despre Noi</h3>
                <p className={styles.cardDescription}>
                  Află mai multe despre platforma eisservice.ro și despre cum
                  putem să te ajutăm
                </p>
              </motion.div>

              <motion.div
                className={styles.helpfulCard}
                variants={fadeInRight}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                onClick={() => navigate("/contact")}
              >
                <div className={styles.cardIcon}>
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className={styles.cardTitle}>Contact</h3>
                <p className={styles.cardDescription}>
                  Contactează-ne pentru întrebări, suport sau pentru a ne spune
                  cum te putem ajuta
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </>
  );
};

export default NotFoundPage;
